import { createSlice, createEntityAdapter } from "@reduxjs/toolkit";

import {
  doGetAllRoutePlans,
  doCreateRoutePlan,
  doUpdateRoutePlan,
  doDetailRoutePlan,
  doDeleteRoutePlan,
  doDetailRoutePlanNoFetching,
  doAddOrdersRoutePlan,
  doCreateRoutePlanNonRedirect,
} from "./thunk";

const isActionWithPending = action => {
  return (
    action.type.startsWith("routePlans/") &&
    action.type.endsWith("/pending") &&
    !action.type.includes("doLockDriver") &&
    !action.type.includes("doDetailRoutePlanNoFetching")
  );
};

const isActionWithFulfilled = action => {
  return (
    action.type.startsWith("routePlans/") &&
    action.type.endsWith("/fulfilled") &&
    !action.type.includes("doOptimizeRoutePlan") &&
    !action.type.includes("doUpdateAndOptimizeRoutePlan") &&
    !action.type.includes("doReorderRoute")
  );
};

const isActionWithFailed = action => {
  return (
    action.type.startsWith("routePlans/") &&
    action.type.endsWith("/rejected") &&
    !action.type.includes("doOptimizeRoutePlan") &&
    !action.type.includes("doUpdateAndOptimizeRoutePlan") &&
    !action.type.includes("doReorderRoute")
  );
};

const routePlansAdapter = createEntityAdapter({
  selectId: item => item._id,
  sortComparer: (a, b) => b.createdAt.localeCompare(a.createdAt),
});

const routePlanSlice = createSlice({
  name: "routePlan",
  initialState: {
    message: null,
    isFetching: false,
    item: null,
    metadata: {},
    entities: [],
    ids: [],
  },
  reducers: {
    handleFailedOptimize: state => {
      state.isFetching = false;
    },
    handleFetching: state => {
      state.isFetching = true;
    },
  },
  extraReducers: builder => {
    builder.addCase(doGetAllRoutePlans.fulfilled, (state, action) => {
      const { routePlans, meta } = action.payload;
      state.metadata = meta;
      routePlansAdapter.setAll(state, routePlans);
    });

    builder.addCase(doCreateRoutePlan.fulfilled, (state, action) => {
      state.message = `create_routeplan_succeeded_${action.payload._id}`;
      routePlansAdapter.upsertOne(state, action.payload);
    });

    builder.addCase(doCreateRoutePlanNonRedirect.fulfilled, (state, action) => {
      state.message = `create_route_plan_succeeded_${action.payload._id}`;
      routePlansAdapter.upsertOne(state, action.payload);
    });

    builder.addCase(doUpdateRoutePlan.fulfilled, (state, action) => {
      routePlansAdapter.upsertOne(state, action.payload);
    });

    builder.addCase(doDetailRoutePlan.fulfilled, (state, action) => {
      state.item = action.payload;
    });

    builder.addCase(doDetailRoutePlanNoFetching.fulfilled, (state, action) => {
      state.item = action.payload;
    });

    builder.addCase(doDeleteRoutePlan.fulfilled, (state, action) => {
      routePlansAdapter.removeOne(state, action.meta.arg);
    });

    builder.addCase(doAddOrdersRoutePlan.fulfilled, state => {
      state.message = "add_orders_to_routeplan_succeeded";
    });

    builder.addMatcher(isActionWithPending, state => {
      state.isFetching = true;
      state.message = null;
    });
    builder.addMatcher(isActionWithFulfilled, state => {
      state.isFetching = false;
    });
    builder.addMatcher(isActionWithFailed, state => {
      state.isFetching = false;
      state.message = null;
    });
  },
});

export const {
  selectAll: selectAllRoutePlans,
  selectById: selectRoutePlanById,
  selectIds: selectRoutePlanIds,
} = routePlansAdapter.getSelectors(state => state.routePlan);

const { actions, reducer: routePlanReducer } = routePlanSlice;
export const { handleFailedOptimize, handleFetching } = actions;

export default routePlanReducer;
